import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import Seo from "@/components/Seo";
import Services from "@/components/landing/Services";
import Gallery from "@/components/landing/Gallery";
import About from "@/components/landing/About";
import Contact from "@/components/landing/Contact";

const SITE = "https://djmagix.net";

const sections = {
  services: Services,
  gallery: Gallery,
  about: About,
  contact: Contact,
};

export default function SeoPage({ page }) {
  const Section = page.section ? sections[page.section] : null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: page.h1, item: `${SITE}${page.slug}` },
        ],
      },
      {
        "@type": "Service",
        name: page.h1,
        description: page.description,
        url: `${SITE}${page.slug}`,
        provider: { "@id": `${SITE}/#business` },
        areaServed: { "@type": "Country", name: "Svizzera" },
      },
    ],
  };

  return (
    <>
      <Seo title={page.title} description={page.description} path={page.slug} image={page.image} jsonLd={jsonLd} />

      {/* Breadcrumb visibile */}
      <nav aria-label="Breadcrumb" className="pt-24 max-w-4xl mx-auto px-6">
        <ol className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#9F8A52]">
          <li>
            <Link to="/" className="hover:text-[#F7DE8B] transition-colors">Home</Link>
          </li>
          <li aria-hidden="true" className="text-[#E2B857]">›</li>
          <li aria-current="page" className="text-[#E2C882]">{page.eyebrow}</li>
        </ol>
      </nav>

      <section className="relative pt-10 pb-20 overflow-hidden">
        <img
          src={page.image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#080808]" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.p
            className="text-xs uppercase tracking-[0.3em] text-[#BFA45D] font-medium"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {page.eyebrow}
          </motion.p>
          <motion.h1
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight gold-text mt-4"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {page.h1}
          </motion.h1>
          <motion.p
            className="font-accent italic text-lg sm:text-xl text-[#E2C882]/85 mt-5 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.25 }}
          >
            {page.intro}
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            <Link
              to="/preventivo"
              data-testid="seo-page-cta-quote"
              className="gold-btn rounded-full px-8 py-3.5 text-sm font-bold uppercase tracking-[0.15em]"
            >
              Preventivo Gratuito
            </Link>
            <a
              href="tel:+41764411359"
              data-testid="seo-page-cta-call"
              className="inline-flex items-center gap-2 rounded-full gold-frame bg-black/40 px-8 py-3.5 text-sm uppercase tracking-[0.15em] text-[#F7DE8B] hover:bg-[#E2B857]/10 transition-colors"
            >
              <Phone size={16} /> 076 441 13 59
            </a>
          </motion.div>
        </div>
      </section>

      <section className="py-14 max-w-4xl mx-auto px-6">
        {page.paragraphs.map((p) => (
          <motion.div
            key={p.h2}
            className="mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#F7DE8B]">
              {p.h2}
            </h2>
            <p className="text-base text-[#E2C882]/85 leading-relaxed mt-3">{p.text}</p>
          </motion.div>
        ))}
      </section>

      {page.relatedLinks && (
        <nav aria-label="Pagine correlate" className="max-w-4xl mx-auto px-6 pb-14">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#BFA45D] mb-4">
            Ti potrebbe interessare
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2.5">
            {page.relatedLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-sm text-[#E2C882]/85 underline decoration-[#BFA45D]/40 underline-offset-4 hover:text-[#F7DE8B] transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </nav>
      )}

      {Section && <Section />}
    </>
  );
}
